import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputAdornment from "@mui/material/InputAdornment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "../components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { useDispatch, useSelector } from "react-redux";
import { Register } from "store/slice/usersSlice";
import Alert from "components/Alert/Alert";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { EmailVerificationAction } from "store/slice/usersSlice";
import { userActions } from "store/slice/usersSlice";
import Select, { SelectChangeEvent } from '@mui/material/Select'
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';

import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";


function RegisterForm() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const usersStore = useSelector(store => store.user);
  const { pathname, search } = useLocation();
  
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');
  
  useEffect(() => {
    if (token) {
     dispatch(EmailVerificationAction(token))
    }
  }, [token])

  useEffect(() => {
    return () => {
      dispatch(userActions.reset())
    }
  },[])

  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone: Yup.string().required().label('Phone'),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
    shift: Yup.number().required("Shift is required"),
    workdays: Yup.number().required("Workdays are required").min(1, "Must be between 1 and 7").max(7, "Must be between 1 and 7"),
    restDays: Yup.number().required("Rest days are required").min(1, "Must be between 1 and 7").max(7, "Must be between 1 and 7"),
    canTakePartTime: Yup.boolean(),
    partTimeAvailability: Yup.string().when("canTakePartTime", {
      is: true,
      then: (schema) => schema.required("Please select availability"),
      otherwise: (schema) => schema.notRequired(),
    }),

  });

  // Form submission handler
  const handleSubmit = (values, { resetForm }) => {
    dispatch(Register(values))
      .unwrap() 
      .then((result) => {
        resetForm();
      }).catch((error) => {
      });;
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign up
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <MDTypography variant="h5" fontWeight="medium" color="white" mt={1}>
              Create Your Account
            </MDTypography>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {usersStore.error.status ? <Alert  message={t(usersStore.error?.message)}  />: ''}
          {usersStore.success.status ? <Alert  message={t(usersStore.success?.message)} isSuccess={true} />: ''}
          {usersStore.emailVerError.status ? <Alert  message={t(usersStore.emailVerError?.message)}  />: ''}
          {usersStore.emailVerSeucces.status ? <Alert  message={t(usersStore.emailVerSeucces?.message)} isSuccess={true} />: ''}
          <Formik
            initialValues={{
              name: "",
              email: "",
              phone: "",
              password: "",
              confirmPassword: "",
              shift: 60,
              workdays: 4,
              restDays: 2,
              canTakePartTime: false,
              partTimeAvailability: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form>
                {/* Name, Email, Password, Confirm Password Fields */}
                
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Name"
                    name="name"
                    fullWidth
                    value={values.name}
                    onChange={handleChange}
                    helperText={
                      touched.name && errors.name ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.name}
                        </span>
                      ) : null
                    }
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDInput
                    type="email"
                    label="Email"
                    name="email"
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    helperText={
                      touched.email && errors.email ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.email}
                        </span>
                      ) : null
                    }
                  />
                </MDBox>
                
                <MDBox mb={2}>
                  <MDInput
                    type="phone"
                    label="Pahone"
                    name="phone"
                    fullWidth
                    value={values.phone}
                    onChange={handleChange}
                    helperText={
                      touched.phone && errors.phone ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.phone}
                        </span>
                      ) : null
                    }
                  />
                </MDBox>

                {/* Shift Field */}
                <MDBox mb={2}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Shift</InputLabel>
                    <Select
                     labelId="demo-simple-select-label"
                      name="shift"
                      label="Shift"
                      value={values.shift}
                      onChange={handleChange}
                      style={{ width: '100%', borderRadius: '4px' }}
                    >
                       <MenuItem value={1}>Morning</MenuItem>
                       <MenuItem value={2}>Night</MenuItem>
                       <MenuItem value={60}>Full</MenuItem>
                    </Select>
                  </FormControl>
                  {touched.shift && errors.shift && (
                    <span style={{ color: "red" }}>{errors.shift}</span>
                  )}
                </MDBox>
                {/* Can Take Part-Time Checkbox */}
                  <MDBox mb={2}>
                    <FormControl fullWidth>
                      <InputLabel id="canTakePartTime">Can Take Part-Time?</InputLabel>
                      <Select
                        labelId="canTakePartTime"
                        name="canTakePartTime"
                        label="Can Take Part-Time?"
                        value={values.canTakePartTime ? "yes" : "no"}
                        style={{ width: '100%', borderRadius: '4px' }}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "canTakePartTime",
                              value: e.target.value === "yes",
                            },
                          })
                        }
                        fullWidth
                      >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>

                  {/* Part-Time Availability Dropdown (only if canTakePartTime is true) */}
                  {values.canTakePartTime && (
                    <MDBox mb={2}>
                      <FormControl fullWidth>
                        <InputLabel>Part-Time Availability</InputLabel>
                        <Select
                          name="partTimeAvailability"
                          label="Part-Time Availability"
                          value={values.partTimeAvailability}
                          onChange={handleChange}
                          fullWidth
                        >
                          <MenuItem value="morning_first">Morning First</MenuItem>
                          <MenuItem value="morning_second">Morning Second</MenuItem>
                          <MenuItem value="morning_any">Morning Any</MenuItem>
                          <MenuItem value="night_first">Night First</MenuItem>
                          <MenuItem value="night_second">Night Second</MenuItem>
                          <MenuItem value="night_any">Night Any</MenuItem>
                          <MenuItem value="any">Any</MenuItem>
                        </Select>
                      </FormControl>
                      {touched.partTimeAvailability && errors.partTimeAvailability && (
                        <FormHelperText error>{errors.partTimeAvailability}</FormHelperText>
                      )}
                    </MDBox>
                  )}

                {/* Workdays Field */}
                <MDBox mb={2}>
                  <MDInput
                    type="number"
                    label="Workdays"
                    name="workdays"
                    fullWidth
                    value={values.workdays}
                    onChange={handleChange}
                    helperText={
                      touched.workdays && errors.workdays ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.workdays}
                        </span>
                      ) : null
                    }
                  />
                </MDBox>

                {/* Rest Days Field */}
                <MDBox mb={2}>
                  <MDInput
                    type="number"
                    label="Rest Days"
                    name="restDays"
                    fullWidth
                    value={values.restDays}
                    onChange={handleChange}
                    helperText={
                      touched.restDays && errors.restDays ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.restDays}
                        </span>
                      ) : null
                    }
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    name="password"
                    fullWidth
                    value={values.password}
                    onChange={handleChange}
                    helperText={
                      touched.password && errors.password ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.password}
                        </span>
                      ) : null
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDInput
                    type={showConfirmPassword ? "text" : "password"}
                    label="Confirm Password"
                    name="confirmPassword"
                    fullWidth
                    value={values.confirmPassword}
                    onChange={handleChange}
                    helperText={
                      touched.confirmPassword && errors.confirmPassword ? (
                        <span style={{ color: "red", display: "flex", alignItems: "center" }}>
                          {errors.confirmPassword}
                        </span>
                      ) : null
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </MDBox>

                {/* Submit Button */}
                <MDBox mt={4} mb={1}>
                  <MDButton type="submit" variant="gradient" color="info" fullWidth>
                    Sign up
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>

          <MDBox mt={3} mb={1} textAlign="center">
            <MDTypography variant="button" color="text">
              Already have an account?{" "}
              <MDTypography
                component={Link}
                to="/auth/login"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default RegisterForm;
